/**
 * Collection Variants JavaScript
 * Masina Diamonds - Displays individual variants with filtering
 * 
 * Features:
 * - Display each variant as its own card
 * - Filter by variant options (Metal Color, Width, Finish, Karat)
 * - Price range filtering
 * - Search functionality
 * - Sorting
 * - Responsive mobile drawer
 * - Lazy loading / Load more
 */

(function() {
  'use strict';

  class CollectionVariants {
    constructor() {
      this.container = document.querySelector('.collection-variants');
      if (!this.container) return;

      this.dataElement = document.getElementById('cv-collection-data');
      if (!this.dataElement) return;

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (e) {
        console.error('Collection Variants: Failed to parse collection data', e);
        return;
      }

      this.products = this.data.products || [];
      this.settings = this.data.settings || {};
      this.allVariants = [];
      this.filteredVariants = [];
      this.displayedVariants = [];
      
      this.currentPage = 1;
      this.variantsPerPage = this.settings.variantsPerPage || 28;
      
      this.filters = {
        metal_color: [],
        width: [],
        finish: [],
        karat: [],
        price_min: this.settings.minPrice / 100,
        price_max: this.settings.maxPrice / 100,
        search: ''
      };
      
      this.sortOrder = 'default';
      
      this.init();
    }

    init() {
      this.flattenVariants();
      this.bindEvents();
      this.initPriceSlider();
      this.applyFilters();
      this.hideLoading();
    }

    /**
     * Flatten all variants from all products into a single array
     * Each variant becomes its own displayable item
     */
    flattenVariants() {
      this.allVariants = [];
      
      this.products.forEach(product => {
        if (product.variants && product.variants.length > 0) {
          product.variants.forEach(variant => {
            // Skip out-of-stock variants - don't show them at all
            if (!variant.available) return;
            
            // Create a combined variant object with product info
            const variantItem = {
              productId: product.id,
              productHandle: product.handle,
              productTitle: product.title,
              productUrl: product.url,
              productOptions: product.options,
              productTags: product.tags || [],
              productVendor: product.vendor,
              productFeaturedImage: product.featured_image,
              
              variantId: variant.id,
              variantTitle: variant.title,
              variantSku: variant.sku,
              variantOptions: variant.options,
              variantPrice: variant.price,
              variantComparePrice: variant.compare_at_price,
              variantAvailable: variant.available,
              variantImage: variant.image || product.featured_image,
              variantUrl: variant.url || `${product.url}?variant=${variant.id}`,
              
              // Create option map for easy filtering
              optionMap: {}
            };
            
            // Map option names to values
            if (product.options && variant.options) {
              product.options.forEach((optionName, index) => {
                variantItem.optionMap[optionName.toLowerCase()] = variant.options[index];
              });
            }
            
            this.allVariants.push(variantItem);
          });
        }
      });
      
      this.filteredVariants = [...this.allVariants];
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Filter toggles (expand/collapse)
      this.container.querySelectorAll('.cv-filter__header').forEach(header => {
        header.addEventListener('click', (e) => this.handleFilterToggle(e));
      });

      // Checkbox filters
      this.container.querySelectorAll('.cv-filter__checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e));
      });

      // Price inputs
      const priceMinInput = document.getElementById('cv-price-min');
      const priceMaxInput = document.getElementById('cv-price-max');
      
      if (priceMinInput) {
        priceMinInput.addEventListener('change', (e) => this.handlePriceInputChange(e, 'min'));
      }
      if (priceMaxInput) {
        priceMaxInput.addEventListener('change', (e) => this.handlePriceInputChange(e, 'max'));
      }

      // Search
      const searchInput = document.getElementById('cv-search');
      if (searchInput) {
        let searchTimeout;
        searchInput.addEventListener('input', (e) => {
          clearTimeout(searchTimeout);
          searchTimeout = setTimeout(() => {
            this.filters.search = e.target.value.toLowerCase();
            this.applyFilters();
          }, 300);
        });
      }

      // Sort
      const sortSelect = document.getElementById('cv-sort');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          this.sortOrder = e.target.value;
          this.applyFilters();
        });
      }

      // Clear filters buttons
      const clearBtn = document.getElementById('cv-clear-filters');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => this.clearAllFilters());
      }

      const clearAllBtn = document.getElementById('cv-clear-all-filters');
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', () => this.clearAllFilters());
      }

      const resetBtn = document.getElementById('cv-reset-filters');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => this.clearAllFilters());
      }

      // Load more
      const loadMoreBtn = document.getElementById('cv-load-more');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', () => this.loadMore());
      }

      // Mobile filter drawer
      const filterToggle = document.getElementById('cv-filter-toggle');
      const filterDrawer = document.getElementById('cv-filter-drawer');
      
      if (filterToggle && filterDrawer) {
        filterToggle.addEventListener('click', () => this.openFilterDrawer());
        
        filterDrawer.querySelectorAll('[data-drawer-close]').forEach(closeBtn => {
          closeBtn.addEventListener('click', () => this.closeFilterDrawer());
        });
      }

      // Clone filters to drawer on init
      this.cloneFiltersToDrawer();
    }

    /**
     * Initialize price range slider
     */
    initPriceSlider() {
      this.initPriceSliderForContainer(document);
    }
    
    /**
     * Initialize price slider for a specific container (main or drawer)
     */
    initPriceSliderForContainer(container) {
      const sliderMin = container.getElementById ? container.getElementById('cv-price-slider-min') : container.querySelector('#cv-price-slider-min, #drawer-cv-price-slider-min');
      const sliderMax = container.getElementById ? container.getElementById('cv-price-slider-max') : container.querySelector('#cv-price-slider-max, #drawer-cv-price-slider-max');
      const range = container.getElementById ? container.getElementById('cv-price-range') : container.querySelector('#cv-price-range, #drawer-cv-price-range');
      const inputMin = container.getElementById ? container.getElementById('cv-price-min') : container.querySelector('#cv-price-min, #drawer-cv-price-min');
      const inputMax = container.getElementById ? container.getElementById('cv-price-max') : container.querySelector('#cv-price-max, #drawer-cv-price-max');

      if (!sliderMin || !sliderMax || !range) return;

      const updateSlider = () => {
        const minVal = parseInt(sliderMin.value);
        const maxVal = parseInt(sliderMax.value);
        const minPercent = ((minVal - sliderMin.min) / (sliderMin.max - sliderMin.min)) * 100;
        const maxPercent = ((maxVal - sliderMax.min) / (sliderMax.max - sliderMax.min)) * 100;
        
        range.style.left = minPercent + '%';
        range.style.width = (maxPercent - minPercent) + '%';
        
        if (inputMin) inputMin.value = minVal;
        if (inputMax) inputMax.value = maxVal;
      };

      // Use both input and touchmove events for better mobile support
      const handleMinInput = () => {
        if (parseInt(sliderMin.value) > parseInt(sliderMax.value)) {
          sliderMin.value = sliderMax.value;
        }
        updateSlider();
      };
      
      const handleMaxInput = () => {
        if (parseInt(sliderMax.value) < parseInt(sliderMin.value)) {
          sliderMax.value = sliderMin.value;
        }
        updateSlider();
      };

      sliderMin.addEventListener('input', handleMinInput);
      sliderMax.addEventListener('input', handleMaxInput);
      
      // Touch events for better mobile responsiveness
      sliderMin.addEventListener('touchmove', handleMinInput, { passive: true });
      sliderMax.addEventListener('touchmove', handleMaxInput, { passive: true });

      let sliderTimeout;
      const applySliderFilter = () => {
        clearTimeout(sliderTimeout);
        sliderTimeout = setTimeout(() => {
          this.filters.price_min = parseInt(sliderMin.value);
          this.filters.price_max = parseInt(sliderMax.value);
          this.applyFilters();
          this.syncPriceSliders();
        }, 300);
      };

      sliderMin.addEventListener('change', applySliderFilter);
      sliderMax.addEventListener('change', applySliderFilter);
      sliderMin.addEventListener('touchend', applySliderFilter);
      sliderMax.addEventListener('touchend', applySliderFilter);

      updateSlider();
    }

    /**
     * Sync all price sliders (desktop and mobile) to match current filter values
     */
    syncPriceSliders() {
      const updateSet = (container) => {
        const sliderMin = container.getElementById ? container.getElementById('cv-price-slider-min') : container.querySelector('#cv-price-slider-min, #drawer-cv-price-slider-min');
        const sliderMax = container.getElementById ? container.getElementById('cv-price-slider-max') : container.querySelector('#cv-price-slider-max, #drawer-cv-price-slider-max');
        const range = container.getElementById ? container.getElementById('cv-price-range') : container.querySelector('#cv-price-range, #drawer-cv-price-range');
        const inputMin = container.getElementById ? container.getElementById('cv-price-min') : container.querySelector('#cv-price-min, #drawer-cv-price-min');
        const inputMax = container.getElementById ? container.getElementById('cv-price-max') : container.querySelector('#cv-price-max, #drawer-cv-price-max');
        
        if (sliderMin && sliderMax && range) {
          sliderMin.value = this.filters.price_min;
          sliderMax.value = this.filters.price_max;
          
          if (inputMin) inputMin.value = this.filters.price_min;
          if (inputMax) inputMax.value = this.filters.price_max;
          
          const minPercent = ((sliderMin.value - sliderMin.min) / (sliderMin.max - sliderMin.min)) * 100;
          const maxPercent = ((sliderMax.value - sliderMax.min) / (sliderMax.max - sliderMax.min)) * 100;
          
          range.style.left = minPercent + '%';
          range.style.width = (maxPercent - minPercent) + '%';
        }
      };
      
      updateSet(document);
      const drawer = document.getElementById('cv-drawer-filters');
      if (drawer) updateSet(drawer);
    }

    /**
     * Handle filter section toggle
     */
    handleFilterToggle(e) {
      const header = e.currentTarget;
      const isExpanded = header.getAttribute('aria-expanded') === 'true';
      header.setAttribute('aria-expanded', !isExpanded);
    }

    /**
     * Handle checkbox filter change
     */
    handleCheckboxChange(e) {
      const checkbox = e.target;
      const filterType = checkbox.name;
      const value = checkbox.value;

      if (checkbox.checked) {
        if (!this.filters[filterType]) {
          this.filters[filterType] = [];
        }
        if (!this.filters[filterType].includes(value)) {
          this.filters[filterType].push(value);
        }
      } else {
        if (this.filters[filterType]) {
          this.filters[filterType] = this.filters[filterType].filter(v => v !== value);
        }
      }

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Handle price input change
     */
    handlePriceInputChange(e, type) {
      const value = parseInt(e.target.value);
      
      if (type === 'min') {
        this.filters.price_min = value;
      } else {
        this.filters.price_max = value;
      }

      this.applyFilters();
      this.syncPriceSliders();
    }

    /**
     * Apply all active filters to variants
     */
    applyFilters() {
      this.filteredVariants = this.allVariants.filter(variant => {
        // Price filter
        const price = variant.variantPrice / 100;
        if (price < this.filters.price_min || price > this.filters.price_max) {
          return false;
        }

        // Search filter
        if (this.filters.search) {
          const searchTerms = this.filters.search.toLowerCase();
          const searchableText = [
            variant.productTitle,
            variant.variantTitle,
            variant.variantSku,
            variant.productVendor,
            ...variant.productTags,
            ...Object.values(variant.optionMap)
          ].filter(Boolean).join(' ').toLowerCase();
          
          if (!searchableText.includes(searchTerms)) {
            return false;
          }
        }

        // Metal Color filter
        if (this.filters.metal_color && this.filters.metal_color.length > 0) {
          const hasColor = this.variantMatchesOption(variant, ['color', 'metal'], this.filters.metal_color);
          if (!hasColor) return false;
        }

        // Width filter
        if (this.filters.width && this.filters.width.length > 0) {
          const hasWidth = this.variantMatchesOption(variant, ['width'], this.filters.width);
          if (!hasWidth) return false;
        }

        // Finish filter
        if (this.filters.finish && this.filters.finish.length > 0) {
          const hasFinish = this.variantMatchesOption(variant, ['finish', 'style'], this.filters.finish);
          if (!hasFinish) return false;
        }

        // Karat filter
        if (this.filters.karat && this.filters.karat.length > 0) {
          const hasKarat = this.variantMatchesOption(variant, ['karat'], this.filters.karat);
          if (!hasKarat) return false;
        }

        return true;
      });

      // Sort variants
      this.sortVariants();

      // Reset pagination
      this.currentPage = 1;

      // Update UI
      this.updateVariantCount();
      this.updateActiveFilters();
      this.renderVariants();
    }

    /**
     * Check if variant matches any of the filter option values
     */
    variantMatchesOption(variant, optionNameParts, filterValues) {
      for (const optionKey in variant.optionMap) {
        const matchesOptionName = optionNameParts.some(part => 
          optionKey.toLowerCase().includes(part.toLowerCase())
        );
        
        if (matchesOptionName) {
          const optionValue = variant.optionMap[optionKey];
          const matches = filterValues.some(filterValue => 
            optionValue && optionValue.toLowerCase().includes(filterValue.toLowerCase())
          );
          if (matches) return true;
        }
      }
      return false;
    }

    /**
     * Sort variants based on current sort order
     */
    sortVariants() {
      switch (this.sortOrder) {
        case 'title-asc':
          this.filteredVariants.sort((a, b) => {
            const titleA = `${a.productTitle} ${a.variantTitle}`;
            const titleB = `${b.productTitle} ${b.variantTitle}`;
            return titleA.localeCompare(titleB);
          });
          break;
        case 'title-desc':
          this.filteredVariants.sort((a, b) => {
            const titleA = `${a.productTitle} ${a.variantTitle}`;
            const titleB = `${b.productTitle} ${b.variantTitle}`;
            return titleB.localeCompare(titleA);
          });
          break;
        case 'price-asc':
          this.filteredVariants.sort((a, b) => a.variantPrice - b.variantPrice);
          break;
        case 'price-desc':
          this.filteredVariants.sort((a, b) => b.variantPrice - a.variantPrice);
          break;
        case 'default':
        default:
          // Keep original order
          break;
      }
    }

    /**
     * Render variants to the grid
     */
    renderVariants() {
      const grid = document.getElementById('cv-products-grid');
      const emptyState = document.getElementById('cv-empty');
      const pagination = document.getElementById('cv-pagination');
      
      if (!grid) return;

      const endIndex = this.currentPage * this.variantsPerPage;
      this.displayedVariants = this.filteredVariants.slice(0, endIndex);

      if (this.filteredVariants.length === 0) {
        grid.innerHTML = '';
        if (emptyState) emptyState.style.display = 'flex';
        if (pagination) pagination.style.display = 'none';
        return;
      }

      if (emptyState) emptyState.style.display = 'none';

      grid.innerHTML = this.displayedVariants.map((variant, index) => 
        this.renderVariantCard(variant, index)
      ).join('');

      // Show/hide load more button
      if (pagination) {
        pagination.style.display = this.displayedVariants.length < this.filteredVariants.length ? 'flex' : 'none';
      }
    }

    /**
     * Render a single variant card
     */
    renderVariantCard(variant, index) {
      const price = this.formatMoney(variant.variantPrice);
      const comparePrice = variant.variantComparePrice > variant.variantPrice 
        ? this.formatMoney(variant.variantComparePrice) 
        : null;
      
      const isOnSale = variant.variantComparePrice > variant.variantPrice;
      const isSoldOut = !variant.variantAvailable;

      let badge = '';
      if (isSoldOut) {
        badge = '<span class="cv-variant-card__badge cv-variant-card__badge--soldout">Sold Out</span>';
      } else if (isOnSale) {
        badge = '<span class="cv-variant-card__badge cv-variant-card__badge--sale">Sale</span>';
      }

      const image = variant.variantImage || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3Crect fill="%23F8F7F2" width="1" height="1"/%3E%3C/svg%3E';

      // Build variant options as the main title (e.g., "White Gold / 6mm / Polished")
      const variantOptionsTitle = variant.variantOptions
        .filter(opt => opt && opt !== 'Default Title')
        .join(' / ');
      
      // Use variant options as title, fallback to product title if no options
      const displayTitle = variantOptionsTitle || variant.productTitle;
      
      // Collection title from the data
      const collectionTitle = this.data.collection ? this.data.collection.title : '';

      return `
        <article class="cv-variant-card cv-fade-in" style="animation-delay: ${Math.min(index * 0.05, 0.4)}s" data-variant-id="${variant.variantId}">
          <a href="${variant.variantUrl}" class="cv-variant-card__link">
            <div class="cv-variant-card__image-wrapper">
              ${badge}
              <img 
                src="${image}" 
                alt="${this.escapeHtml(displayTitle)}" 
                class="cv-variant-card__image"
                loading="lazy"
              >
            </div>
            <div class="cv-variant-card__info">
              <h3 class="cv-variant-card__title">${this.escapeHtml(displayTitle)}</h3>
              ${collectionTitle ? `<p class="cv-variant-card__collection">${this.escapeHtml(collectionTitle)}</p>` : ''}
              <div class="cv-variant-card__price">
                ${comparePrice ? `<span class="cv-variant-card__price-compare">${comparePrice}</span>` : ''}
                <span class="cv-variant-card__price-current">${price}</span>
              </div>
            </div>
          </a>
        </article>
      `;
    }

    /**
     * Load more variants
     */
    loadMore() {
      this.currentPage++;
      this.renderVariants();
      
      const grid = document.getElementById('cv-products-grid');
      if (grid) {
        const cards = grid.querySelectorAll('.cv-variant-card');
        const targetCard = cards[cards.length - this.variantsPerPage];
        if (targetCard) {
          targetCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }

    /**
     * Update variant count display
     */
    updateVariantCount() {
      const countEl = document.getElementById('cv-variant-count');
      const drawerCountEl = document.getElementById('cv-drawer-count');
      
      if (countEl) {
        countEl.textContent = this.filteredVariants.length;
      }
      if (drawerCountEl) {
        drawerCountEl.textContent = this.filteredVariants.length;
      }
    }

    /**
     * Update active filters display
     */
    updateActiveFilters() {
      const container = document.getElementById('cv-active-filters');
      const list = document.getElementById('cv-active-filters-list');
      const badge = document.getElementById('cv-active-filters-count');
      
      if (!container || !list) return;

      let activeFilters = [];

      // Metal color filters
      if (this.filters.metal_color && this.filters.metal_color.length > 0) {
        this.filters.metal_color.forEach(value => {
          activeFilters.push({ type: 'metal_color', value, label: `Color: ${value}` });
        });
      }

      // Width filters
      if (this.filters.width && this.filters.width.length > 0) {
        this.filters.width.forEach(value => {
          activeFilters.push({ type: 'width', value, label: `Width: ${value}` });
        });
      }

      // Finish filters
      if (this.filters.finish && this.filters.finish.length > 0) {
        this.filters.finish.forEach(value => {
          activeFilters.push({ type: 'finish', value, label: `Finish: ${value}` });
        });
      }

      // Karat filters
      if (this.filters.karat && this.filters.karat.length > 0) {
        this.filters.karat.forEach(value => {
          activeFilters.push({ type: 'karat', value, label: `Karat: ${value}` });
        });
      }

      // Price filter
      const defaultMinPrice = this.settings.minPrice / 100;
      const defaultMaxPrice = this.settings.maxPrice / 100;
      
      if (this.filters.price_min > defaultMinPrice || this.filters.price_max < defaultMaxPrice) {
        activeFilters.push({ 
          type: 'price', 
          value: 'range', 
          label: `$${this.filters.price_min} - $${this.filters.price_max}` 
        });
      }

      // Search filter
      if (this.filters.search) {
        activeFilters.push({ type: 'search', value: this.filters.search, label: `"${this.filters.search}"` });
      }

      if (activeFilters.length === 0) {
        container.style.display = 'none';
        if (badge) badge.textContent = '';
        return;
      }

      container.style.display = 'flex';
      if (badge) badge.textContent = activeFilters.length;

      list.innerHTML = activeFilters.map(filter => `
        <span class="cv-active-filter-tag">
          ${this.escapeHtml(filter.label)}
          <button type="button" data-remove-filter="${filter.type}" data-filter-value="${this.escapeHtml(filter.value)}" aria-label="Remove filter">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </button>
        </span>
      `).join('');

      // Bind remove filter events
      list.querySelectorAll('[data-remove-filter]').forEach(btn => {
        btn.addEventListener('click', () => {
          const type = btn.dataset.removeFilter;
          const value = btn.dataset.filterValue;
          this.removeFilter(type, value);
        });
      });
    }

    /**
     * Remove a specific filter
     */
    removeFilter(type, value) {
      if (type === 'price') {
        this.filters.price_min = this.settings.minPrice / 100;
        this.filters.price_max = this.settings.maxPrice / 100;
        
        this.syncPriceSliders();
      } else if (type === 'search') {
        this.filters.search = '';
        const searchInput = document.getElementById('cv-search');
        if (searchInput) searchInput.value = '';
      } else if (this.filters[type]) {
        this.filters[type] = this.filters[type].filter(v => v !== value);
        
        const checkbox = this.container.querySelector(`input[name="${type}"][value="${value}"]`);
        if (checkbox) checkbox.checked = false;
      }

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.filters = {
        metal_color: [],
        width: [],
        finish: [],
        karat: [],
        price_min: this.settings.minPrice / 100,
        price_max: this.settings.maxPrice / 100,
        search: ''
      };

      // Uncheck all filter checkboxes
      this.container.querySelectorAll('.cv-filter__checkbox').forEach(checkbox => {
        checkbox.checked = false;
      });

      // Reset price slider
      this.syncPriceSliders();

      // Reset search
      const searchInput = document.getElementById('cv-search');
      if (searchInput) searchInput.value = '';

      this.applyFilters();
      this.syncDrawerFilters();
    }

    /**
     * Clone filters to mobile drawer
     */
    cloneFiltersToDrawer() {
      const sidebarFilters = document.getElementById('cv-filters-form');
      const drawerBody = document.getElementById('cv-drawer-filters');
      
      if (!sidebarFilters || !drawerBody) return;

      const filters = sidebarFilters.querySelectorAll('.cv-filter');
      filters.forEach(filter => {
        const clone = filter.cloneNode(true);
        
        clone.querySelectorAll('[id]').forEach(el => {
          el.id = 'drawer-' + el.id;
        });
        clone.querySelectorAll('[for]').forEach(el => {
          el.setAttribute('for', 'drawer-' + el.getAttribute('for'));
        });
        
        drawerBody.appendChild(clone);
      });

      drawerBody.querySelectorAll('.cv-filter__header').forEach(header => {
        header.addEventListener('click', (e) => this.handleFilterToggle(e));
      });

      drawerBody.querySelectorAll('.cv-filter__checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', (e) => this.handleDrawerCheckboxChange(e));
      });
      
      // Initialize price slider in drawer
      this.initPriceSliderForContainer(drawerBody);
    }

    /**
     * Handle checkbox change in drawer
     */
    handleDrawerCheckboxChange(e) {
      const checkbox = e.target;
      const filterType = checkbox.name;
      const value = checkbox.value;

      const sidebarCheckbox = this.container.querySelector(
        `.cv-sidebar input[name="${filterType}"][value="${value}"]`
      );
      if (sidebarCheckbox) {
        sidebarCheckbox.checked = checkbox.checked;
      }

      this.handleCheckboxChange(e);
    }

    /**
     * Sync drawer filters with sidebar filters
     */
    syncDrawerFilters() {
      const drawerBody = document.getElementById('cv-drawer-filters');
      if (!drawerBody) return;

      drawerBody.querySelectorAll('.cv-filter__checkbox').forEach(drawerCheckbox => {
        const name = drawerCheckbox.name;
        const value = drawerCheckbox.value;
        
        const sidebarCheckbox = this.container.querySelector(
          `.cv-sidebar input[name="${name}"][value="${value}"]`
        );
        if (sidebarCheckbox) {
          drawerCheckbox.checked = sidebarCheckbox.checked;
        }
      });
    }

    /**
     * Open mobile filter drawer
     */
    openFilterDrawer() {
      const drawer = document.getElementById('cv-filter-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.syncDrawerFilters();
      }
    }

    /**
     * Close mobile filter drawer
     */
    closeFilterDrawer() {
      const drawer = document.getElementById('cv-filter-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }

    /**
     * Hide loading state
     */
    hideLoading() {
      const loading = document.getElementById('cv-loading');
      if (loading) {
        loading.style.display = 'none';
      }
    }

    /**
     * Format price according to shop money format
     */
    formatMoney(cents) {
      const amount = cents / 100;
      const format = this.settings.moneyFormat || '${{amount}}';
      
      let formatted = format.replace('{{amount}}', amount.toFixed(2));
      formatted = formatted.replace('{{amount_no_decimals}}', Math.round(amount));
      formatted = formatted.replace('{{amount_with_comma_separator}}', amount.toFixed(2).replace('.', ','));
      
      return formatted;
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CollectionVariants());
  } else {
    new CollectionVariants();
  }
})();
